// Port of lib/config.py.
//
// The Python module re-reads PROJECT_ROOT/openai.json on every call so edits take effect
// without a restart. The static site cannot re-read disk, so the bundled openai.json
// (resolveJsonModule import) is the DEFAULT config and a runtime override can be stored in
// the storage backend at the same logical path ("openai.json"). `.env` loading has no
// browser equivalent; env vars are emulated with the `set_env`/`_env_get` seam below.

import openaiConfigDefault from "../../openai.json";
import { getStorage } from "./storage";

export const DEFAULT_MODEL = "chat-latest";
/** Logical storage path of the runtime config override (Python: PROJECT_ROOT/openai.json). */
export const OPENAI_CONFIG_PATH = "openai.json";
export const AI_WEIGHT = 0.4;
export const DETERMINISTIC_WEIGHT = 0.6;
export const OPENAI_INPUT_COST_ENV = "OPENAI_INPUT_COST_PER_1M_TOKENS";
export const OPENAI_OUTPUT_COST_ENV = "OPENAI_OUTPUT_COST_PER_1M_TOKENS";
export const OPENAI_CACHED_INPUT_COST_ENV = "OPENAI_CACHED_INPUT_COST_PER_1M_TOKENS";
/**
 * Cached input tokens (prompt-cache hits) are billed at a fraction of the normal input rate.
 * Used only when neither `openai.json` nor the env var states an explicit cached rate — OpenAI's
 * standing discount for the GPT-5 family is 90% off input, i.e. 0.10 × the input rate.
 */
export const DEFAULT_CACHED_INPUT_DISCOUNT = 0.1;

/** Storage prefixes replacing the Python USERS_DIR / INSTRUCTIONS_DIR path constants. */
export const USERS_DIR = "users";
export const INSTRUCTIONS_DIR = "instructions";

// ── Environment-variable seam ────────────────────────────────────────────────────────────
// Python reads os.environ (OPENAI_MODEL, OPENAI_*_COST_PER_1M_TOKENS). The browser has no
// process.env, so the port keeps a module-level map that the app (or tests) can populate.

const _env = new Map<string, string>();

export function set_env(name: string, value: string | null): void {
  if (value === null) _env.delete(name);
  else _env.set(name, value);
}

function _env_get(name: string, fallback = ""): string {
  return _env.get(name) ?? fallback;
}

export const BIRTH_TIME_OPTIONS: Record<string, string> = {
  recorded: "I know my recorded time of birth",
  approximate: "I know an approximate time",
  range: "Select from a range",
  unknown: "No idea",
};

export const EVENT_CATEGORIES: string[] = [
  "award",
  "career_breakthrough",
  "career_contract",
  "career_failure",
  "career_learning",
  "child_birth",
  "death_family",
  "education_start",
  "injury",
  "intimacy_relationship",
  "marriage",
  "mood_low",
  // Loss of a pet/companion animal (6th house — pets/small animals). Fully wired in the scoring
  // tables since the Python port but previously missing from this catalog, so the intake form and
  // AI extraction could never emit it (fixed 2026-07-02).
  "pet_death",
  "property_purchase",
  "relationship_conflict",
  "relocation",
  "travel_family",
  "anxiety_health",
  "adventure_positive",
  "social_celebration",
];

// ── OpenAI call-type catalog ──────────────────────────────────────────────────────────────
// The canonical set of schema_names used as call-type keys in openai.json.
// test_config verifies that openai.json's "calls" section matches this set exactly.
// Initial intake questions are static (lib/intake_questions) — not a separate call.
export const OPENAI_CALL_TYPES: ReadonlySet<string> = new Set([
  "rectification_fact_extraction",
  "trait_chat",
  "photo_traits",
  "time_compare_chat",
]);

function _is_plain_object(value: any): value is Record<string, any> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Set (or clear, with null) the runtime OpenAI config override. The override replaces the
 * bundled openai.json entirely — the static site's equivalent of editing the file on disk.
 */
export function set_openai_config_override(payload: Record<string, any> | string | null): void {
  const storage = getStorage();
  if (payload === null) storage.remove(OPENAI_CONFIG_PATH);
  else storage.write(OPENAI_CONFIG_PATH, typeof payload === "string" ? payload : JSON.stringify(payload, null, 2));
}

/**
 * The raw parsed config: the storage override if present (corrupt/non-object → {}, matching
 * the Python unreadable-file behaviour), else the bundled openai.json. Read on every call so
 * override edits take effect without a reload.
 */
export function load_openai_config(): Record<string, any> {
  const raw = getStorage().read(OPENAI_CONFIG_PATH);
  if (raw !== null) {
    let data: any;
    try {
      data = JSON.parse(raw);
    } catch {
      return {};
    }
    return _is_plain_object(data) ? data : {};
  }
  return _is_plain_object(openaiConfigDefault) ? (openaiConfigDefault as Record<string, any>) : {};
}

/**
 * The OpenAI model + reasoning to use for one call type.
 *
 * ``openai.json`` shape (per-call models, with a shared fallback)::
 *
 *     {
 *       "models": {"gpt-5.4": {"input_cost_per_1m": 1.25, "output_cost_per_1m": 10}},
 *       "default": {"model": "gpt-5.4", "reasoning": "medium"},
 *       "calls": {
 *         "trait_chat": {"model": "gpt-5.4-mini", "reasoning": null},
 *         ...
 *       }
 *     }
 *
 * Resolution for ``call_type``: ``calls[call_type]`` overrides ``default`` overrides the
 * ``OPENAI_MODEL`` env var overrides ``DEFAULT_MODEL``. The legacy flat shape
 * (``{"model": ..., "reasoning": ...}`` at the root) is still honoured as the default block, so
 * old configs keep working. ``reasoning`` is normalised to the object form the Responses API needs
 * (a bare ``"medium"`` → ``{"effort": "medium"}``); ``null`` means "send no reasoning param".
 */
export function load_openai_settings(call_type: string | null = null): Record<string, any> {
  const data = load_openai_config();
  const default_ = _is_plain_object(data["default"]) ? data["default"] : {};
  const calls = _is_plain_object(data["calls"]) ? data["calls"] : {};
  const call_cfg = call_type && _is_plain_object(calls[call_type]) ? calls[call_type] : {};

  // model: per-call → default block → legacy flat root → env → DEFAULT_MODEL
  const model =
    call_cfg["model"] || default_["model"] || data["model"] || _env_get("OPENAI_MODEL") || DEFAULT_MODEL;
  // reasoning: per-call wins if the key is present (so an explicit null can switch it OFF for one
  // call), else the default block, else the legacy flat root.
  let reasoning_raw: any;
  if ("reasoning" in call_cfg) {
    reasoning_raw = call_cfg["reasoning"];
  } else if ("reasoning" in default_) {
    reasoning_raw = default_["reasoning"];
  } else {
    reasoning_raw = data["reasoning"];
  }
  return { model, reasoning: normalize_reasoning(reasoning_raw) };
}

/** Python float(): throws on non-numeric strings / non-numbers (Number("") is 0 in JS!). */
function _py_float(value: any): number {
  if (typeof value === "boolean") return value ? 1 : 0;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const s = value.trim();
    if (s === "") throw new Error(`could not convert string to float: '${value}'`);
    const n = Number(s);
    if (Number.isNaN(n)) throw new Error(`could not convert string to float: '${value}'`);
    return n;
  }
  throw new Error("float() argument must be a string or a real number");
}

/** A numeric config value if usable, else the named env var, else 0.0. */
function _config_float(value: any, env_name: string): number {
  if (value !== null && value !== undefined) {
    try {
      return _py_float(value);
    } catch {
      // fall through to the env var
    }
  }
  try {
    return _py_float(_env_get(env_name, ""));
  } catch {
    return 0.0;
  }
}

/**
 * Per-1M-token input/cached-input/output cost for ``model`` from ``openai.json``'s root ``models``
 * map, falling back to the OPENAI_*_COST_PER_1M_TOKENS env vars, then 0. Accepts either
 * ``input_cost_per_1m``/``output_cost_per_1m``/``cached_input_cost_per_1m`` or the short
 * ``input``/``output``/``cached_input`` keys.
 *
 * ``cached_input_cost_per_1m`` is the rate for prompt-cache HITS (the Responses API reports them
 * as ``usage.input_tokens_details.cached_tokens``, counted inside ``input_tokens``). When it is
 * not configured anywhere it defaults to DEFAULT_CACHED_INPUT_DISCOUNT × the input rate rather
 * than 0, so a cached-heavy conversation is never costed as free.
 */
export function model_pricing(model: string | null): Record<string, number> {
  const data = load_openai_config();
  const models = _is_plain_object(data["models"]) ? data["models"] : {};
  const entry = model && _is_plain_object(models[model]) ? models[model] : {};
  const input_cost_per_1m = _config_float(
    "input_cost_per_1m" in entry ? entry["input_cost_per_1m"] : entry["input"],
    OPENAI_INPUT_COST_ENV,
  );
  const cached_raw = "cached_input_cost_per_1m" in entry ? entry["cached_input_cost_per_1m"] : entry["cached_input"];
  const cached_configured =
    (cached_raw !== null && cached_raw !== undefined) || _env_get(OPENAI_CACHED_INPUT_COST_ENV, "") !== "";
  return {
    input_cost_per_1m,
    cached_input_cost_per_1m: cached_configured
      ? _config_float(cached_raw, OPENAI_CACHED_INPUT_COST_ENV)
      : input_cost_per_1m * DEFAULT_CACHED_INPUT_DISCOUNT,
    output_cost_per_1m: _config_float(
      "output_cost_per_1m" in entry ? entry["output_cost_per_1m"] : entry["output"],
      OPENAI_OUTPUT_COST_ENV,
    ),
  };
}

/**
 * Coerce a reasoning config into the object shape the Responses API expects.
 *
 * Accepts the object form ``{"effort": "medium"}`` (passed through), the bare-string shorthand
 * ``"medium"`` (→ ``{"effort": "medium"}``), or anything falsy/unrecognised (→ ``None``).
 */
export function normalize_reasoning(reasoning: any): Record<string, any> | null {
  if (typeof reasoning === "string") {
    const effort = reasoning.trim();
    return effort ? { effort } : null;
  }
  if (_is_plain_object(reasoning)) {
    return Object.keys(reasoning).length ? reasoning : null;
  }
  return null;
}
